import React from 'react';
import { TextField, type TextFieldProps } from '@mui/material';
import { TokenPickerPopover } from './TokenPickerPopover';
import { useTokenFieldContext } from './TokenFieldContext';

/**
 * A drop-in replacement for MUI `TextField` that adds the `{{`-token trigger to any campaign-admin text
 * field with a one-line import alias and no other call-site change. It forwards every MUI prop through
 * unchanged; when the author types `{{` in an eligible field it auto-completes the closing `}}` and opens
 * {@link TokenPickerPopover} anchored to the field, and a pick replaces the whole `{{…}}` region with the
 * chosen canonical token — the same behavior as {@link TokenTextField}, but for the many existing fields
 * whose `onChange` is event-based (`(e) => …e.target.value`) rather than the runner's `(next) => …`.
 *
 * Because the enhancement is applied by *shadowing the import* (so it also covers a file's incidental
 * fields), it self-guards: it enhances only a controlled text field (`typeof value === 'string'` and an
 * `onChange`) that isn't `select`, `disabled`, or a non-text `type` (number/date/password/…). Everything
 * else — including a `<TextField {...params} />` inside an `Autocomplete` (which passes neither top-level
 * `value` nor `onChange`) — falls straight through to plain MUI `TextField`, untouched. The campaign and
 * step-order it offers variables for come from {@link TokenFieldContext} (overridable via props).
 */

/** Input `type`s that are not free text — never enhanced. */
const NON_TEXT_TYPES = new Set([
  'number',
  'date',
  'datetime-local',
  'time',
  'month',
  'week',
  'password',
  'email',
  'tel',
  'url',
  'color',
  'file',
  'range',
  'checkbox',
  'radio',
]);

/** Set a controlled input's value the way a user keystroke would, so React/RHF `onChange` fires naturally. */
function dispatchNativeInput(el: HTMLInputElement | HTMLTextAreaElement, value: string): void {
  const proto =
    el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  setter?.call(el, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

type Ref<T> = React.Ref<T> | undefined;

/** Fan a node out to every ref a call site (and this component) supplied. */
function useMergedInputRef(external: Ref<HTMLInputElement | HTMLTextAreaElement>) {
  const own = React.useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const merged = React.useCallback(
    (node: HTMLInputElement | HTMLTextAreaElement | null) => {
      own.current = node;
      if (typeof external === 'function') external(node);
      else if (external && typeof external === 'object')
        (external as React.MutableRefObject<unknown>).current = node;
    },
    [external],
  );
  return { own, merged };
}

export type TokenAwareTextFieldProps = TextFieldProps & {
  /** Override the campaign whose variables the picker offers (defaults to TokenFieldContext). */
  campaignId?: number;
  /** Override the step-order availability gate (defaults to TokenFieldContext). */
  uptoStepOrder?: number;
  /** Force the token trigger off even on an otherwise-eligible field. */
  disableTokens?: boolean;
};

export const TokenAwareTextField = React.forwardRef<HTMLDivElement, TokenAwareTextFieldProps>(
  function TokenAwareTextField(props, ref) {
    const ctx = useTokenFieldContext();
    const { campaignId: campaignIdProp, uptoStepOrder: uptoProp, disableTokens, ...tf } = props;
    const campaignId = campaignIdProp ?? ctx.campaignId;
    const uptoStepOrder = uptoProp ?? ctx.uptoStepOrder;

    const { own: inputEl, merged: mergedInputRef } = useMergedInputRef(
      tf.inputRef as Ref<HTMLInputElement | HTMLTextAreaElement>,
    );
    const [open, setOpen] = React.useState(false);
    const braceRef = React.useRef(0);
    const prevRef = React.useRef(typeof tf.value === 'string' ? tf.value : '');

    const eligible =
      !disableTokens &&
      !tf.select &&
      !tf.disabled &&
      !NON_TEXT_TYPES.has(String(tf.type ?? 'text')) &&
      typeof tf.value === 'string' &&
      typeof tf.onChange === 'function';

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const next = e.target.value;
        const caret = e.target.selectionStart ?? next.length;
        const prev = prevRef.current;
        prevRef.current = next;

        // Pass the keystroke through to the field's own handler first (controlled / RHF stay in sync).
        (tf.onChange as React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>)(e);

        // A single typed `{` completing `{{` right before the caret opens the picker. The length check
        // keeps a paste of "{{" — and the recursive change from our own `}}` insert — from re-triggering.
        const typedSingleChar = next.length === prev.length + 1;
        const justOpenedBraces = caret >= 2 && next[caret - 1] === '{' && next[caret - 2] === '{';
        if (!typedSingleChar || !justOpenedBraces) return;

        braceRef.current = caret - 2;
        const el = inputEl.current;
        if (el && next.slice(caret, caret + 2) !== '}}') {
          const withClose = next.slice(0, caret) + '}}' + next.slice(caret);
          // Defer to after the controlled re-render paints `next`, then inject the closing braces as a
          // native input so the field's own onChange commits them; leave the caret between the braces.
          requestAnimationFrame(() => {
            dispatchNativeInput(el, withClose);
            try {
              el.setSelectionRange(caret, caret);
            } catch {
              /* best-effort caret restore */
            }
          });
        }
        setOpen(true);
      },
      [tf.onChange, inputEl],
    );

    const pick = React.useCallback(
      (token: string) => {
        const el = inputEl.current;
        setOpen(false);
        if (!el) return;
        const v = el.value;
        const bracePos = braceRef.current;
        // Replace the whole `{{…}}` region (braces + any partial) with the chosen canonical token.
        const closeIdx = v.indexOf('}}', bracePos + 2);
        const endIdx = closeIdx === -1 ? Math.min(bracePos + 2, v.length) : closeIdx + 2;
        const nextValue = v.slice(0, bracePos) + token + v.slice(endIdx);
        dispatchNativeInput(el, nextValue);
        const caret = bracePos + token.length;
        requestAnimationFrame(() => {
          el.focus();
          try {
            el.setSelectionRange(caret, caret);
          } catch {
            /* best-effort caret restore */
          }
        });
      },
      [inputEl],
    );

    if (!eligible) {
      return <TextField ref={ref} {...tf} />;
    }

    return (
      <>
        <TextField ref={ref} {...tf} onChange={handleChange} inputRef={mergedInputRef} />
        <TokenPickerPopover
          open={open}
          anchorEl={inputEl.current}
          campaignId={campaignId}
          uptoStepOrder={uptoStepOrder}
          onPick={pick}
          onClose={() => setOpen(false)}
        />
      </>
    );
  },
);

export default TokenAwareTextField;
